import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Box, alpha, Typography } from '@mui/material';
import {
  Home as HomeIcon,
  Assignment as TodoIcon,
  People as LeadsIcon,
  Business as ClientsIcon,
  School as TutorsIcon,
  Event as SessionsIcon,
  Assessment as ReportsIcon,
  Inventory as CatalogIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface CommandItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
  group: 'navigation' | 'actions' | 'recent';
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) {
          onClose();
        }
      }
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleSelect = useCallback(
    (action: () => void) => {
      action();
      onClose();
      setSearch('');
    },
    [onClose]
  );

  const navigationCommands: CommandItem[] = [
    {
      id: 'nav-home',
      label: 'Go to Home',
      icon: <HomeIcon />,
      action: () => navigate('/'),
      keywords: ['dashboard', 'main'],
      group: 'navigation',
    },
    {
      id: 'nav-todos',
      label: 'Go to Todos',
      icon: <TodoIcon />,
      action: () => navigate('/todos'),
      keywords: ['tasks', 'queue'],
      group: 'navigation',
    },
    {
      id: 'nav-leads',
      label: 'Go to Leads',
      icon: <LeadsIcon />,
      action: () => navigate('/leads'),
      keywords: ['contacts', 'prospects'],
      group: 'navigation',
    },
    {
      id: 'nav-clients',
      label: 'Go to Clients',
      icon: <ClientsIcon />,
      action: () => navigate('/clients'),
      keywords: ['customers', 'accounts'],
      group: 'navigation',
    },
    {
      id: 'nav-tutors',
      label: 'Go to Tutors',
      icon: <TutorsIcon />,
      action: () => navigate('/tutors'),
      keywords: ['teachers', 'instructors'],
      group: 'navigation',
    },
    {
      id: 'nav-sessions',
      label: 'Go to Sessions',
      icon: <SessionsIcon />,
      action: () => navigate('/sessions'),
      keywords: ['appointments', 'meetings'],
      group: 'navigation',
    },
    {
      id: 'nav-reports',
      label: 'Go to Reports',
      icon: <ReportsIcon />,
      action: () => navigate('/reports'),
      keywords: ['analytics', 'metrics'],
      group: 'navigation',
    },
    {
      id: 'nav-catalog',
      label: 'Go to Catalog',
      icon: <CatalogIcon />,
      action: () => navigate('/catalog'),
      keywords: ['products', 'items'],
      group: 'navigation',
    },
  ];

  const actionCommands: CommandItem[] = [
    {
      id: 'action-new-lead',
      label: 'Create new lead',
      icon: <AddIcon />,
      action: () => navigate('/leads/new'),
      keywords: ['add', 'create', 'contact'],
      group: 'actions',
    },
    {
      id: 'action-new-client',
      label: 'Create new client',
      icon: <AddIcon />,
      action: () => navigate('/clients/new'),
      keywords: ['add', 'create', 'customer'],
      group: 'actions',
    },
    {
      id: 'action-phone',
      label: 'Start browser phone call',
      icon: <PhoneIcon />,
      action: () => {
        // TODO: Open phone dialer
        console.log('Opening phone dialer');
      },
      keywords: ['call', 'dial'],
      group: 'actions',
    },
  ];

  // TODO: Get recent items from local storage or API
  const recentCommands: CommandItem[] = [
    {
      id: 'recent-client-1',
      label: 'John Smith · Client · Chicago',
      icon: <ClientsIcon />,
      action: () => navigate('/clients/123'),
      group: 'recent',
    },
    {
      id: 'recent-tutor-1',
      label: 'Michael Lee · Tutor · NYC',
      icon: <TutorsIcon />,
      action: () => navigate('/tutors/456'),
      group: 'recent',
    },
  ];

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        pt: '15vh',
        bgcolor: alpha('#000', 0.5),
      }}
      onClick={onClose}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: '100%',
          maxWidth: 560,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        <Command
          loop
          shouldFilter={true}
          style={{
            width: '100%',
          }}
        >
          {/* Search Input */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Search or type a command..."
              style={{
                width: '100%',
                height: 56,
                border: 'none',
                outline: 'none',
                fontSize: '1rem',
                background: 'transparent',
                fontFamily: 'inherit',
              }}
            />
          </Box>

          {/* Command List */}
          <Command.List
            style={{
              maxHeight: 400,
              overflow: 'auto',
              padding: '8px',
            }}
          >
            <Command.Empty
              style={{
                padding: '24px',
                textAlign: 'center',
                color: '#637381',
              }}
            >
              No results found.
            </Command.Empty>

            {/* Recent */}
            {!search && recentCommands.length > 0 && (
              <Command.Group
                heading={
                  <Typography
                    variant="caption"
                    sx={{ px: 1, color: 'text.secondary', fontWeight: 600 }}
                  >
                    Recent
                  </Typography>
                }
              >
                {recentCommands.map((cmd) => (
                  <CommandItem
                    key={cmd.id}
                    item={cmd}
                    onSelect={() => handleSelect(cmd.action)}
                  />
                ))}
              </Command.Group>
            )}

            {/* Navigation */}
            <Command.Group
              heading={
                <Typography
                  variant="caption"
                  sx={{ px: 1, color: 'text.secondary', fontWeight: 600 }}
                >
                  Navigation
                </Typography>
              }
            >
              {navigationCommands.map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  item={cmd}
                  onSelect={() => handleSelect(cmd.action)}
                />
              ))}
            </Command.Group>

            {/* Actions */}
            <Command.Group
              heading={
                <Typography
                  variant="caption"
                  sx={{ px: 1, color: 'text.secondary', fontWeight: 600 }}
                >
                  Actions
                </Typography>
              }
            >
              {actionCommands.map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  item={cmd}
                  onSelect={() => handleSelect(cmd.action)}
                />
              ))}
            </Command.Group>
          </Command.List>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 2,
              px: 2,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <kbd
                style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: '#f4f6f8',
                  border: '1px solid #ddd',
                  marginRight: 4,
                }}
              >
                ↑↓
              </kbd>
              to navigate
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <kbd
                style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: '#f4f6f8',
                  border: '1px solid #ddd',
                  marginRight: 4,
                }}
              >
                ↵
              </kbd>
              to select
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <kbd
                style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: '#f4f6f8',
                  border: '1px solid #ddd',
                  marginRight: 4,
                }}
              >
                esc
              </kbd>
              to close
            </Typography>
          </Box>
        </Command>
      </Box>
    </Box>
  );
}

// Individual command item component
function CommandItem({
  item,
  onSelect,
}: {
  item: CommandItem;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      value={`${item.label} ${item.keywords?.join(' ') || ''}`}
      onSelect={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: '0.875rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 1,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          '& svg': { fontSize: 18 },
        }}
      >
        {item.icon}
      </Box>
      <span>{item.label}</span>
    </Command.Item>
  );
}
